// src/pages/CompleteRegistration.js
// v6 – adds debug logging for redirect tracing

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

import api from "../services/api";
import { API_PATHS, ROUTES } from "../config";
import ResendRegistrationLinkBanner from "../components/ResendRegistrationLinkBanner";
import "../styles/CompleteRegistration.css";

const CompleteRegistration = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState("");
  const [showResend, setShowResend] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("token") || "";
    setToken(t);

    if (t) {
      try {
        const decoded = jwtDecode(t);
        if (decoded?.email) setEmail(decoded.email);
      } catch {
        // ignore
      }
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setShowResend(false);

    const cleanDisplayName = displayName.trim();

    if (!token) {
      setError("Missing registration token.");
      return;
    }
    if (!cleanDisplayName) {
      setError("Display name is required.");
      return;
    }
    if (cleanDisplayName.length > 255) {
      setError("Display name must be 255 characters or less.");
      return;
    }
    if (!password || password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    setSubmitting(true);

    try {
      const { data } = await api.post(API_PATHS.COMPLETE_REGISTRATION, {
        token,
        display_name: cleanDisplayName,
        new_password: password,
      });

      const access = data?.access_token;
      const refresh = data?.refresh_token;

      if (!access) {
        throw new Error("Registration succeeded but no token returned.");
      }

      localStorage.setItem("token", access);
      if (refresh) localStorage.setItem("refreshToken", refresh);

      const decoded = jwtDecode(access);
      const role = decoded?.role?.toUpperCase() || "";
      const schoolName = decoded?.school_name || "";

      localStorage.setItem("role", role);
      localStorage.setItem("schoolName", schoolName);

      window.dispatchEvent(new Event("gg-auth-changed"));

      // 🔍 DEBUG LOG
      console.log("🚀 COMPLETE REGISTRATION SUCCESS", {
        role,
        email,
        schoolName,
      });

      if (role === "ADMIN") {
        console.log("➡️ Redirecting ADMIN →", ROUTES.ADMIN);
        navigate(ROUTES.ADMIN, { replace: true });
        return;
      }

      if (role === "PRINCIPAL") {
        console.log("➡️ Redirecting PRINCIPAL →", ROUTES.CONTINUE_IN_APP);
        navigate(ROUTES.CONTINUE_IN_APP, {
          replace: true,
          state: {
            email: email || "",
          },
        });
        return;
      }

      if (role === "STAFF") {
        console.log("➡️ Redirecting STAFF →", ROUTES.STAFF_DASHBOARD);
        navigate(ROUTES.STAFF_DASHBOARD, { replace: true });
        return;
      }

      console.log("➡️ Redirecting fallback →", ROUTES.VIEW_PHOTOS);
      navigate(ROUTES.VIEW_PHOTOS, { replace: true });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";

      console.log("❌ COMPLETE REGISTRATION ERROR:", detail);

      setError(typeof detail === "string" ? detail : JSON.stringify(detail));

      if (String(detail).toLowerCase().includes("expired")) {
        setShowResend(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="complete-registration-page">
      <div className="complete-registration-container">
        <h2 className="form-title">Complete your GhrianGhraffy setup</h2>

        {error && (
          <p className="error-message" aria-live="polite">
            {error}
          </p>
        )}

        {showResend && email && (
          <div className="resend-banner-wrapper">
            <ResendRegistrationLinkBanner email={email} />
          </div>
        )}

        <form onSubmit={handleSubmit} className="registration-form">
          <div className="form-group">
            <label>Your email</label>
            <input type="email" value={email || ""} readOnly />
          </div>

          <div className="form-group">
            <label>Display name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <button type="submit" disabled={submitting}>
            {submitting ? "Completing…" : "Finish setup"}
          </button>
        </form>

        <p>
          Already set up? <Link to={ROUTES.LOGIN}>Login</Link>
        </p>
      </div>
    </div>
  );
};

export default CompleteRegistration;